import { LucideIconName } from '@bm-js/atlascloud-shared'
import { Icon } from '@bm-js/atlascloud-ui'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

export interface ModalProps {
  onClose?: () => void
  children: React.ReactNode
  className?: string
  title?: string
  rootId?: string
  zIndex?: number
}

export default function Modal({
  onClose,
  children,
  title,
  className = 'w-full',
  zIndex = 50,
  rootId,
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.()
    }

    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [onClose])

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose?.()
  }

  return createPortal(
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className={`fixed inset-0 bg-black/50 flex items-center justify-center`}
      style={{ zIndex }}
    >
      <div
        className={`bg-white rounded-lg shadow-xl mx-4 max-h-[90vh] flex flex-col relative ${className}`}
      >
        {title && (
          <div className='px-6 py-4 border-b border-primary-100'>
            <h2 className='text-lg font-semibold text-gray-900'>{title}</h2>
          </div>
        )}
        {!!onClose && (
          <button
            className='absolute top-4 right-4 cursor-pointer text-red-500'
            onClick={onClose}
          >
            <Icon name={LucideIconName.X} className='size-5' />
          </button>
        )}
        <div className='px-6 py-4 overflow-y-auto'>{children}</div>
      </div>
    </div>,
    rootId ? document.getElementById(rootId) || document.body : document.body
  )
}
